<template>
  <div
    :class="[
      'flex flex-column relative input-content ',
      !noSpace ? 'gap-2' : null,
    ]"
  >
    <div class="flex gap-1" v-if="props.label">
      <label
        :for="props.name"
        :class="[
          'tv__input-label',
          props.size === 'sm' ? 'tv__input-sm-label' : '',
        ]"
      >
        {{ props.label }}
        <span v-if="props.alias" class="tv__input-label-alias">
          {{ props.alias }}
        </span>
      </label>
      <Tooltip
        v-if="props.tooltipMessage"
        :maxWidthTooltip="300"
        floatTooltipDesk="top"
        floatTooltipMobile="bottom"
        :useMaterialIcon="true"
        iconName="info"
        triggerEvent="click"
      >
        <template #title>
          <p v-html="props.tooltipMessage" />
        </template>
      </Tooltip>
    </div>

    <InputGroup class="tv_currency" :class="inputClasses">
      <InputNumber
        :id="props.id"
        v-model="inputValue"
        :name="props.name"
        :min="0"
        :max="props.max"
        unstyled
        mode="decimal"
        :locale="props.noFormat ? undefined : 'en-US'"
        :inputmode="props.noFormat ? 'numeric' : 'decimal'"
        :minFractionDigits="props.noFormat ? 0 : 0"
        :maxFractionDigits="props.noFormat ? 0 : 5"
        :useGrouping="!props.noFormat"
        :autocomplete="props.autocomplete"
        :aria-describedby="props.id + '-help'"
        :invalid="props.error"
        :placeholder="props.placeholder"
        :disabled="isInputDisabled"
        @input="handleInput"
        class="notranslate currency-input"
        translate="no"
      />
    </InputGroup>

    <i
      v-show="props.error && inputValue !== null"
      :class="[
        'tv__input-btn',
        'tv__input-icon',
        'tv__input-icon-clear',
        props.size === 'sm' ? 'tv__input-sm' : '',
        props.currency ? 'clear-currency' : 'clear-number',
      ]"
      @click="clearInput"
    />
    <i
      v-show="props.disabled && props.canEdit && isInputDisabled"
      :class="[
        'tv__input-btn',
        'tv__input-icon',
        'tv__input-icon-edit',
        props.size === 'sm' ? 'tv__input-sm' : '',
      ]"
      @click="enableInput"
    />

    <span :class="[!noSpace ? 'input-message-content' : null]">
      <p
        style="margin-top: 0.5rem"
        v-if="props.helpMessage && !props.error"
        :class="[
          'tv__input-help',
          props.size === 'sm' ? 'tv__input-help-sm' : '',
        ]"
      >
        {{ props.helpMessage }}
      </p>
      <div
        style="margin-top: 0.5rem"
        v-if="props.error"
        :class="[
          'tv__input-error-message flex mt-0',
          props.size === 'sm' ? 'tv__input-error-message-sm' : '',
        ]"
      >
        <i class="tv__input-icon tv__input-icon-error" />
        <p>
          {{ props.customErrorMessage }}
        </p>
      </div>
    </span>
  </div>
</template>

<script setup>
import { defineProps, computed, defineEmits, watch, ref } from "vue";
import InputNumber from "primevue/inputnumber";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import Tooltip from "./Tooltip.vue";

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  label: String,
  alias: String,
  placeholder: String,
  id: String,
  modelValue: [Number, String, null],
  disabled: Boolean,
  canEdit: Boolean,
  helpMessage: String,
  noSpace: Boolean,
  tooltipMessage: String,
  tooltipDirDesk: String,
  tooltipDirMobile: String,
  tooltipMaxWidth: Number,
  error: Boolean,
  customErrorMessage: String,
  autocomplete: String,
  size: {
    type: String,
    default: "lg",
    validator: (value) => ["lg", "sm"].includes(value),
  },
  mode: {
    type: String,
    default: "decimal", // 'decimal', 'currency'
  },
  min: Number,
  max: Number,
  noFormat: Boolean,
  currency: {
    type: String,
  },
});

const emits = defineEmits(["update:modelValue", "update:withEmptyValue"]);

const inputValue = ref(props.modelValue);
const isInputDisabled = ref(props.disabled);

const inputClasses = computed(() => [
  "tv__input",
  props.error ? "tv__input-error" : null,
  props.canEdit ? "tv__input-edit" : null,
  props.size === "sm" ? "tv__input-sm" : null,
]);

const handleInput = (event) => {
  emits("update:modelValue", event.value);
};

const clearInput = () => {
  inputValue.value = null;
  emits("update:modelValue", null);
  emits("update:withEmptyValue", null);
};

const enableInput = () => {
  isInputDisabled.value = false;
};

watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== inputValue.value) {
      inputValue.value = newValue;
    }
  }
);

watch(
  () => props.disabled,
  (newValue) => {
    isInputDisabled.value = newValue;
  }
);
</script>
